import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  BellIcon,
  ShieldCheckIcon,
  EyeIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

interface Settings {
  monitoring: {
    refreshInterval: number
    retentionDays: number
    alertThreshold: number
    enableRealTime: boolean
  }
  security: {
    sessionTimeout: number
    maxLoginAttempts: number
    enableRateLimit: boolean
    rateLimitWindow: number
  }
  notifications: {
    enableEmailAlerts: boolean
    enableSuspiciousActivity: boolean
    enableSystemHealth: boolean
    emailAddress: string
  }
  display: {
    itemsPerPage: number
    enableAnimations: boolean
    theme: 'light' | 'dark' | 'auto'
    compactView: boolean
  }
}

const SettingsPage = () => {
  const [settings, setSettings] = useState<Settings>({
    monitoring: {
      refreshInterval: 5,
      retentionDays: 30,
      alertThreshold: 100,
      enableRealTime: true
    },
    security: {
      sessionTimeout: 480, // 8 hours in minutes
      maxLoginAttempts: 5,
      enableRateLimit: true,
      rateLimitWindow: 60
    },
    notifications: {
      enableEmailAlerts: false,
      enableSuspiciousActivity: true,
      enableSystemHealth: true,
      emailAddress: ''
    },
    display: {
      itemsPerPage: 20,
      enableAnimations: true,
      theme: 'light',
      compactView: false
    }
  })

  const [loading, setLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [activeSection, setActiveSection] = useState('monitoring')

  const handleSave = async () => {
    setLoading(true)
    setSaveStatus('saving')
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Save to localStorage for demo
      localStorage.setItem('admin_settings', JSON.stringify(settings))
      
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error) {
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all settings to defaults?')) {
      setSettings({
        monitoring: {
          refreshInterval: 5,
          retentionDays: 30,
          alertThreshold: 100,
          enableRealTime: true
        },
        security: {
          sessionTimeout: 480,
          maxLoginAttempts: 5,
          enableRateLimit: true,
          rateLimitWindow: 60
        },
        notifications: {
          enableEmailAlerts: false,
          enableSuspiciousActivity: true,
          enableSystemHealth: true,
          emailAddress: ''
        },
        display: {
          itemsPerPage: 20,
          enableAnimations: true,
          theme: 'light',
          compactView: false
        }
      })
    }
  }

  useEffect(() => {
    // Load settings from localStorage on mount
    const savedSettings = localStorage.getItem('admin_settings')
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings))
      } catch (error) {
        console.error('Failed to load saved settings')
      }
    }
  }, [])

  const sections = [
    { id: 'monitoring', name: 'Monitoring', icon: <ChartBarIcon className="h-5 w-5" /> },
    { id: 'security', name: 'Security', icon: <ShieldCheckIcon className="h-5 w-5" /> },
    { id: 'notifications', name: 'Notifications', icon: <BellIcon className="h-5 w-5" /> },
    { id: 'display', name: 'Display', icon: <EyeIcon className="h-5 w-5" /> }
  ]

  return (
    <>
      <Helmet>
        <title>Settings - CyberScrape Training Platform</title>
        <meta name="description" content="Configure monitoring dashboard settings and preferences" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-600">
              Configure your monitoring dashboard preferences
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {saveStatus === 'saved' && (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="text-sm">Settings saved</span>
              </div>
            )}
            
            {saveStatus === 'error' && (
              <div className="flex items-center space-x-2 text-red-600">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span className="text-sm">Save failed</span>
              </div>
            )}
            
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-accent-500"
            >
              Reset to Defaults
            </button>
            
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-accent-600 border border-transparent rounded-lg hover:bg-accent-700 focus:ring-2 focus:ring-accent-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:w-64">
            <nav className="bg-white rounded-lg shadow p-4">
              <ul className="space-y-2">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors duration-200 ${
                        activeSection === section.id
                          ? 'bg-accent-100 text-accent-800 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {section.icon}
                      <span>{section.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Settings Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow">
              {/* Monitoring Settings */}
              {activeSection === 'monitoring' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <ChartBarIcon className="h-6 w-6 text-accent-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Monitoring Settings</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Auto-refresh Interval (seconds)
                      </label>
                      <select
                        value={settings.monitoring.refreshInterval}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          monitoring: { ...prev.monitoring, refreshInterval: parseInt(e.target.value) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value={1}>1 second</option>
                        <option value={5}>5 seconds</option>
                        <option value={10}>10 seconds</option>
                        <option value={30}>30 seconds</option>
                        <option value={60}>1 minute</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data Retention (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={settings.monitoring.retentionDays}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          monitoring: { ...prev.monitoring, retentionDays: parseInt(e.target.value) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Alert Threshold (requests per minute)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.monitoring.alertThreshold}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          monitoring: { ...prev.monitoring, alertThreshold: parseInt(e.target.value) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="enableRealTime"
                        checked={settings.monitoring.enableRealTime}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          monitoring: { ...prev.monitoring, enableRealTime: e.target.checked }
                        }))}
                        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                      />
                      <label htmlFor="enableRealTime" className="text-sm font-medium text-gray-700">
                        Enable real-time monitoring
                      </label>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Security Settings */}
              {activeSection === 'security' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <ShieldCheckIcon className="h-6 w-6 text-accent-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Security Settings</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Session Timeout (minutes)
                      </label>
                      <input
                        type="number"
                        min="30"
                        max="1440"
                        value={settings.security.sessionTimeout}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          security: { ...prev.security, sessionTimeout: parseInt(e.target.value) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maximum Login Attempts
                      </label>
                      <input
                        type="number"
                        min="3"
                        max="10"
                        value={settings.security.maxLoginAttempts}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          security: { ...prev.security, maxLoginAttempts: parseInt(e.target.value) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rate Limit Window (seconds)
                      </label>
                      <input
                        type="number"
                        min="30"
                        max="300"
                        value={settings.security.rateLimitWindow}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          security: { ...prev.security, rateLimitWindow: parseInt(e.target.value) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="enableRateLimit"
                        checked={settings.security.enableRateLimit}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          security: { ...prev.security, enableRateLimit: e.target.checked }
                        }))}
                        className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                      />
                      <label htmlFor="enableRateLimit" className="text-sm font-medium text-gray-700">
                        Enable rate limiting
                      </label>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Notifications Settings */}
              {activeSection === 'notifications' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <BellIcon className="h-6 w-6 text-accent-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Notification Settings</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={settings.notifications.emailAddress}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, emailAddress: e.target.value }
                        }))}
                        placeholder="admin@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableEmailAlerts"
                          checked={settings.notifications.enableEmailAlerts}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            notifications: { ...prev.notifications, enableEmailAlerts: e.target.checked }
                          }))}
                          className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                        />
                        <label htmlFor="enableEmailAlerts" className="text-sm font-medium text-gray-700">
                          Enable email alerts
                        </label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableSuspiciousActivity"
                          checked={settings.notifications.enableSuspiciousActivity}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            notifications: { ...prev.notifications, enableSuspiciousActivity: e.target.checked }
                          }))}
                          className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                        />
                        <label htmlFor="enableSuspiciousActivity" className="text-sm font-medium text-gray-700">
                          Alert on suspicious activity
                        </label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableSystemHealth"
                          checked={settings.notifications.enableSystemHealth}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            notifications: { ...prev.notifications, enableSystemHealth: e.target.checked }
                          }))}
                          className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                        />
                        <label htmlFor="enableSystemHealth" className="text-sm font-medium text-gray-700">
                          System health notifications
                        </label>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Display Settings */}
              {activeSection === 'display' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <EyeIcon className="h-6 w-6 text-accent-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Display Settings</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Items per Page
                      </label>
                      <select
                        value={settings.display.itemsPerPage}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          display: { ...prev.display, itemsPerPage: parseInt(e.target.value) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Theme
                      </label>
                      <select
                        value={settings.display.theme}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          display: { ...prev.display, theme: e.target.value as 'light' | 'dark' | 'auto' }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="auto">Auto</option>
                      </select>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="enableAnimations"
                          checked={settings.display.enableAnimations}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            display: { ...prev.display, enableAnimations: e.target.checked }
                          }))}
                          className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                        />
                        <label htmlFor="enableAnimations" className="text-sm font-medium text-gray-700">
                          Enable animations
                        </label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="compactView"
                          checked={settings.display.compactView}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            display: { ...prev.display, compactView: e.target.checked }
                          }))}
                          className="rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                        />
                        <label htmlFor="compactView" className="text-sm font-medium text-gray-700">
                          Compact view
                        </label>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SettingsPage
